import { initializeApp } from "firebase/app";
import { getFirestore, doc, setDoc, getDoc, getDocs, collection, deleteDoc } from "firebase/firestore";

// TODO: Replace this object with the config from your Firebase Project Settings
const firebaseConfig = {
  apiKey: "AIzaSyCfQ2pErM761xSMY45C2JRSIIV9Kovy88E",
  authDomain: "carbs-2e51e.firebaseapp.com",
  projectId: "carbs-2e51e",
  storageBucket: "carbs-2e51e.firebasestorage.app",
  messagingSenderId: "857055929843",
  appId: "1:857055929843:web:760cf8f6523d60cc9b5e86",
  measurementId: "G-RVDV57XG1G"
};

let app;
let db;
export let isFirebaseConfigured = false;

// Check if the user has actually pasted their config yet
if (firebaseConfig.apiKey && firebaseConfig.apiKey !== "YOUR_API_KEY") {
    try {
        app = initializeApp(firebaseConfig);
        db = getFirestore(app);
        isFirebaseConfigured = true;
        console.log("🔥 Firebase Database Connected!");
    } catch (e) {
        console.error("Firebase init failed:", e);
    }
} else {
    console.log("ℹ️ Waiting for Firebase Config... running in local mode.");
}

/**
 * Saves a single user to the Firestore "users" collection.
 * Uses merge:true so it only updates fields that have changed, rather than overwriting.
 */
export async function syncUserToCloud(userObj) {
    if (!isFirebaseConfigured) return false;
    
    try {
        const userRef = doc(db, "users", userObj.id);
        await setDoc(userRef, userObj, { merge: true });
        return true;
    } catch (e) {
        console.error("Error syncing to Firebase:", e);
        return false;
    }
}

/**
 * For the Admin Dashboard: Pulls all students from the cloud database
 */
export async function fetchAllCloudUsers() {
    if (!isFirebaseConfigured) return {};
    
    try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const allUsers = {};
        
        querySnapshot.forEach((document) => {
            allUsers[document.id] = document.data();
        });
        
        return allUsers;
    } catch (e) {
        console.error("Error fetching users from Firebase:", e);
        return {};
    }
}

/**
 * Deletes a single user from the cloud database
 */
export async function deleteCloudUser(userId) {
    if (!isFirebaseConfigured) return false;
    try {
        await deleteDoc(doc(db, "users", userId));
        return true;
    } catch (e) {
        console.error("Error deleting user from Firebase:", e);
        return false;
    }
}

/**
 * Nuke all users from the cloud database (DANGER)
 */
export async function nukeCloudUsers() {
    if (!isFirebaseConfigured) return false;
    try {
        const querySnapshot = await getDocs(collection(db, "users"));
        const ops = [];
        querySnapshot.forEach((document) => {
            ops.push(deleteDoc(doc(db, "users", document.id)));
        });
        await Promise.all(ops);
        return true;
    } catch (e) {
        console.error("Error nuking users from Firebase:", e);
        return false;
    }
}

/**
 * Sets the global maintenance mode flag in Firestore
 */
export async function setMaintenanceMode(active, message = "System calibration in progress.") {
    if (!isFirebaseConfigured) {
        localStorage.setItem('carb-maintenance-mode', active);
        localStorage.setItem('carb-maintenance-message', message);
        return true;
    }
    try {
        const configRef = doc(db, "config", "status");
        await setDoc(configRef, { maintenance: active, message: message }, { merge: true });
        return true;
    } catch (e) {
        console.error("Error setting maintenance mode:", e);
        return false;
    }
}

/**
 * Fetches the global maintenance mode status
 */
export async function getMaintenanceMode() {
    if (!isFirebaseConfigured) {
        return {
            active: localStorage.getItem('carb-maintenance-mode') === 'true',
            message: localStorage.getItem('carb-maintenance-message') || "System calibration in progress."
        };
    }
    try {
        const configRef = doc(db, "config", "status");
        const snap = await getDoc(configRef);
        return snap.exists() ? { 
            active: snap.data().maintenance || false, 
            message: snap.data().message || "System calibration in progress."
        } : { active: false, message: "" };
    } catch (e) {
        console.error("Error fetching maintenance mode:", e);
        return { active: false, message: "" };
    }
}

/**
 * Sends a global alert to all connected pilots
 */
export async function broadcastAlert(message) {
    if (!isFirebaseConfigured) {
        localStorage.setItem('carb-global-alert', JSON.stringify({ message, time: Date.now() }));
        return true;
    }
    try {
        const alertRef = doc(db, "config", "alert");
        await setDoc(alertRef, { message, timestamp: serverTimestamp(), id: Math.random().toString(36).substring(7) });
        return true;
    } catch (e) {
        console.error("Error broadcasting alert:", e);
        return false;
    }
}

/**
 * Deletes the current global alert
 */
export async function clearAlert() {
    if (!isFirebaseConfigured) {
        localStorage.removeItem('carb-global-alert');
        return true;
    }
    try {
        const alertRef = doc(db, "config", "alert");
        await deleteDoc(alertRef);
        return true;
    } catch (e) {
        console.error("Error clearing alert:", e);
        return false;
    }
}

/**
 * Updates global feature toggles
 */
export async function setFeatureToggles(toggles) {
    if (!isFirebaseConfigured) {
        localStorage.setItem('carb-feature-toggles', JSON.stringify(toggles));
        return true;
    }
    try {
        const toggleRef = doc(db, "config", "toggles");
        await setDoc(toggleRef, toggles, { merge: true });
        return true;
    } catch (e) {
        console.error("Error setting toggles:", e);
        return false;
    }
}

export async function getFeatureToggles() {
    if (!isFirebaseConfigured) {
        const raw = localStorage.getItem('carb-feature-toggles');
        return raw ? JSON.parse(raw) : {};
    }
    try {
        const toggleRef = doc(db, "config", "toggles");
        const snap = await getDoc(toggleRef);
        return snap.exists() ? snap.data() : {};
    } catch (e) {
        return {};
    }
}

/**
 * Adds an entry to the live mission telemetry log
 */
export async function logTelemetry(eventText) {
    if (!isFirebaseConfigured) return;
    try {
        const logRef = doc(db, "telemetry", "mission-log");
        const snap = await getDoc(logRef);
        let logs = snap.exists() ? snap.data().entries || [] : [];
        logs.unshift({ text: eventText, timestamp: Date.now() });
        logs = logs.slice(0, 50); // Keep last 50
        await setDoc(logRef, { entries: logs });
    } catch (e) {}
}

import { onSnapshot, serverTimestamp } from "firebase/firestore";

/**
 * Listens for mission telemetry updates
 */
export function listenToTelemetry(callback) {
    if (!isFirebaseConfigured) return () => {};
    const logRef = doc(db, "telemetry", "mission-log");
    return onSnapshot(logRef, (snap) => {
        if (snap.exists()) callback(snap.data().entries || []);
    });
}

/**
 * Listens for global alerts
 */
export function listenToAlerts(callback) {
    if (!isFirebaseConfigured) return () => {};
    const alertRef = doc(db, "config", "alert");
    return onSnapshot(alertRef, (snap) => {
        if (snap.exists()) callback(snap.data());
        else callback(null); // Explicitly callback with null if alert is cleared
    });
}

/**
 * Listens for Maintenance and Message updates
 */
export function listenToConfig(callback) {
    if (!isFirebaseConfigured) return () => {};
    const configRef = doc(db, "config", "status");
    const toggleRef = doc(db, "config", "toggles");
    
    onSnapshot(configRef, (snap) => {
        if (snap.exists()) callback({ type: 'status', data: snap.data() });
    });
    onSnapshot(toggleRef, (snap) => {
        if (snap.exists()) callback({ type: 'toggles', data: snap.data() });
    });
}
